import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { isMobile } from "../../globalStyles/mobile";
import { customerService } from "../../utils/services/customerService";

const emptyAddress = {
  address1: "",
  address2: "",
  city: "",
  province: "",
  province_code: "",
  country: "",
  country_code: "",
  zip: "",
};

const CustomerModal = ({ open, handleClose, onAddData, selectedCustomer }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    currency: "",
  });
  const [addressData, setAddressData] = useState(emptyAddress);
  const [formErrors, setFormErrors] = useState({});
  const clientId = localStorage.getItem("client_id");

  useEffect(() => {
    if (open) {
      if (selectedCustomer) {
        const { name, email, first_name, last_name, phone, currency, address } =
          selectedCustomer;
        setFormData({
          name: name || "",
          email: email || "",
          first_name: first_name || "",
          last_name: last_name || "",
          phone: phone || "",
          currency: currency || "",
        });
        const addr = address?.[0] || {};
        setAddressData({
          address1: addr.address1 || "",
          address2: addr.address2 || "",
          city: addr.city || "",
          province: addr.province || "",
          province_code: addr.province_code || "",
          country: addr.country || "",
          country_code: addr.country_code || "",
          zip: addr.zip || "",
        });
      } else {
        setFormData({
          name: "",
          email: "",
          first_name: "",
          last_name: "",
          phone: "",
          currency: "",
        });
        setAddressData(emptyAddress);
      }
      setFormErrors({});
    }
  }, [open, selectedCustomer]);

  const validate = () => {
    const errors = {};
    if (!formData.name) errors.name = "Name is required";
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      const payload = {
        ...formData,
        address: [{ ...addressData }],
        ...(clientId ? { clients: [clientId] } : {}),
      };
      if (selectedCustomer) {
        await customerService.edit(selectedCustomer._id, payload);
        toast.success("Customer updated successfully");
      } else {
        await customerService.create(payload);
        toast.success("Customer created successfully");
      }
      onAddData && onAddData();
      handleClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Operation failed");
    }
  };

  const textField = (label, key, required, obj, setObj, multiline) => (
    <Grid item xs={12} sm={6}>
      <CustomTypography fontSize={13}>
        {label}
        {required && <span style={{ color: "red" }}> *</span>}
      </CustomTypography>
      <TextField
        size="small"
        autoFocus={false}
        margin="dense"
        variant="outlined"
        fullWidth
        multiline={!!multiline}
        rows={multiline || 1}
        value={obj[key] || ""}
        onChange={(e) => setObj((prev) => ({ ...prev, [key]: e.target.value }))}
        error={!!formErrors[key]}
        helperText={formErrors[key]}
      />
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ style: { maxHeight: "90vh" } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle fontSize={20} fontFamily="poppins" fontWeight={500}>
          {selectedCustomer ? "Edit Customer" : "Add Customer"}
        </DialogTitle>
        <IconButton onClick={handleClose} style={{ marginRight: 8 }}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          {textField("Name", "name", true, formData, setFormData)}
          {textField("Email", "email", true, formData, setFormData)}
          {textField("First Name", "first_name", false, formData, setFormData)}
          {textField("Last Name", "last_name", false, formData, setFormData)}
          {textField("Phone", "phone", false, formData, setFormData)}
          {textField("Currency", "currency", false, formData, setFormData)}
        </Grid>

        <Divider style={{ marginTop: 16, marginBottom: 8 }} />
        <CustomTypography fontSize={14} fontWeight={600}>
          Address
        </CustomTypography>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          {textField(
            "Address Line 1",
            "address1",
            false,
            addressData,
            setAddressData,
          )}
          {textField(
            "Address Line 2",
            "address2",
            false,
            addressData,
            setAddressData,
          )}
          {textField("City", "city", false, addressData, setAddressData)}
          {textField(
            "Province",
            "province",
            false,
            addressData,
            setAddressData,
          )}
          {textField(
            "Province Code",
            "province_code",
            false,
            addressData,
            setAddressData,
          )}
          {textField("Country", "country", false, addressData, setAddressData)}
          {textField(
            "Country Code",
            "country_code",
            false,
            addressData,
            setAddressData,
          )}
          {textField("Zip", "zip", false, addressData, setAddressData)}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions style={{ padding: "12px 24px" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          style={{ textTransform: "none", marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          style={{
            textTransform: "none",
            background: "#DC585C",
            color: "#fff",
          }}
        >
          {selectedCustomer ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerModal;
